const array = value => Array.isArray(value) ? value : [];
const text = value => typeof value === 'string' ? value.trim() : '';
const enabled = item => item?.registered === true && item.enabled === true && item.available === true;
const currentRegistry = availability => availability?.status === 'ready' ? availability.projection : null;
const rootsFor = entry => [entry, entry?.action, entry?.payload, entry?.action?.payload].filter(Boolean);
const identifiers = (roots, keys) => [...new Set(roots.flatMap(root => keys.map(key => text(root[key]))).filter(Boolean))];
const selectedBindings = (roots, registry) => {
  const skills = identifiers(roots, ['skill_id']);
  const capabilities = identifiers(roots, ['selected_capability_id', 'capability_id']);
  return skills.every(id => array(registry?.skills).some(item => item.target_id === id && enabled(item)))
    && capabilities.every(id => array(registry?.available_capability_ids).includes(id)
      && array(registry?.capabilities).some(item => item.target_id === id && enabled(item)))
    && skills.every(id => capabilities.every(capability => array(registry?.skills)
      .some(item => item.target_id === id && array(item.capability_ids).includes(capability))));
};
const hasBinding = roots => identifiers(roots, ['skill_id', 'selected_capability_id', 'capability_id']).length > 0;
const invalidBinding = roots => roots.some(root => ['skill_id', 'selected_capability_id', 'capability_id']
  .some(key => Object.hasOwn(root, key) && !text(root[key])));
const moduleBindings = (roots, modules) => {
  const keys = identifiers(roots, ['module_key', 'moduleKey']);
  const commands = identifiers(roots, ['command']);
  const modes = identifiers(roots, ['target_mode']);
  return array(modules).filter(module => keys.includes(text(module.module_key))
    || array(module.action_commands).some(command => commands.includes(command))
    || [module.target_mode, ...array(module.target_modes)].some(mode => modes.includes(text(mode))));
};

/** Presentation intersection only: declared bindings + the accepted FLARE activation snapshot. */
export const isCapabilityEntryAvailable = (entry, availability, modules = []) => {
  const roots = rootsFor(entry);
  if (invalidBinding(roots)) return false;
  const boundModules = moduleBindings(roots, modules);
  const registry = currentRegistry(availability);
  const explicitModules = identifiers(roots, ['module_key', 'moduleKey']);
  if (explicitModules.some(key => !boundModules.some(module => module.module_key === key))) return false;
  return (!hasBinding(roots) || Boolean(registry) && selectedBindings(roots, registry))
    && boundModules.every(module => isModuleAvailable(module, availability));
};

const isModuleAvailable = (module, availability) => {
  const binding = module?.message_action;
  const roots = [module, binding].filter(Boolean);
  return binding?.enabled !== false && hasBinding(roots) && !invalidBinding(roots)
    && Boolean(currentRegistry(availability)) && selectedBindings(roots, currentRegistry(availability));
};
export const selectAvailableModules = (modules, availability) => array(modules)
  .filter(module => isModuleAvailable(module, availability));
export const selectAvailableCapabilityCatalog = (entries, availability) => array(entries)
  .filter(entry => text(entry?.skill_id) && isCapabilityEntryAvailable(entry, availability));
export const selectAvailableCapabilityEntries = (entries, availability, modules = []) => array(entries)
  .filter(entry => isCapabilityEntryAvailable(entry, availability, modules));
